/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import * as jsonp from './jsonp';
import * as fetch from './utils/fetch';
import adrotation from './adrotation';
import bm from './bidmanager';
import adserver from './adserver';
import adloader from './adloader';
import storage from './utils/storage';

/**
 * @description Implements ad configuration functionality
 * @exports adconfig
 * @requires  module:utils
 * @requires  module:jsonp
 * @requires  module:adrotation
 * @requires  module:bidmanager
 * @requires  module:adserver
 * @requires  module:adloader
 * @requires  module:utils/Storage
 */
class _adconfig {
  _win: window;
  _doc: window.document;
  _expiryTimeoutSec: number;
  _loadTimeout: number;
  _url: string;
  _pageInfo: pageInfo;
  _lastConfigLoadTime: number;
  _gPageInfo: pageInfo;
  _config: adConfig;
  _configOverride: {};
  constructor() {
    this._win = window;
    this._doc = document;
    this._expiryTimeoutSec = 20 * 60;
    this._loadTimeout = 3000;
    this._url = '';
    /**
     * @global
     * @typedef adConfig
     * @property {Object} settings
     * @property {rotationConfig} settings.adRotate
     * @property {Object.<string, hbPartnerConfig>} settings.headerBidding
     * @property {Object.<string, posConfigInfo>} settings.posMap
     * @property {Object.<string, perAdServerConfig>} settings.adServer
     * @property {string} settings.loggerGif
     * @property {number} settings.configExpirtySec
     * @property {number} settings.loadingDelay
     */
    this._config = {
      settings: {
        adRotate: {},
        headerBidding: {},
        loggerGif: '//static.uolcontent.com/images/spacer.gif',
        configExpirySec: 20 * 60,
        posMap: {},
        adServer: {},
        adloading: {}
      }
    };
    this._pageInfo = {
      posList: [],
      name: ''
    };
    this._lastConfigLoadTime = 0;
    this._gPageInfo = {
      posList: [],
      name: ''
    };
    this._configOverride = {};
  }

  /**
   * Initialize the object
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._loadTimeout = g.adConfig.loadTimeout;
    this._url = g.adConfig.url;
    this._configOverride = g.adConfig.override || {};
    this._gPageInfo = g.pageInfo;
    u.setLoggerGif(this._config.settings.loggerGif);
  }

  /**
   * Sets pageName & posList. posList is always cached as Array.
   * @param {string} [pageName] - Pagename
   * @param {string|Array<string>} [posList] - Position list
   * @returns {undefined}
   */
  setPageInfo(pageName: ?string, posList: ?(string | Array<string>)) {
    this._pageInfo.name = pageName || '';
    posList = posList || '';
    if (u.isString(posList) && posList.length) {
      //$FlowFixMe
      this._pageInfo.posList = posList.split(',');
    } else if (u.isArray(posList) && posList.length) {
      this._pageInfo.posList = posList;
    }
  }

  /**
   * @global
   * @type function
   * @callback getPageNameCB
   * @param {Object} context
   */
  /**
   * Getter function for pagename
   * @param {string|getPageNameCB} s - input can be string or function
   * @return {string} - Page name
   */
  getPageName(s: ?string | (window => string)): string {
    s = s || this._pageInfo.name || this._gPageInfo.name;
    if (u.isString(s)) {
      // $FlowFixMe
      return s;
    } else if (u.isFunction(s)) {
      // $FlowFixMe
      return s.call(this._win);
    }
    return '';
  }

  /**
   * @global
   * @type function
   * @callback getPagePositionsCB
   * @param {Object} context
   * @param {string} page - pagename
   */
  /**
   * Getter function for ad positions for the current page
   * @param {string|Array<string>|getPagePositionsCB} [v] - list of ad positions
   * @param {string|getPageNameCB} pageName - current page name
   * @param {boolean} [bRotation=false] - whether called during ad rotation
   * @return {Array<string>} - Ad positions
   */
  getPagePositions(
    v: ?(string | Array<string> | ((window, string) => string)),
    pageName: ?(string | (window => string)),
    bRotation: ?boolean
  ): Array<string> {
    if (!v && this._pageInfo.posList.length) {
      v = this._pageInfo.posList;
    }
    if (!v) {
      v = this._gPageInfo.posList;
    }
    if (u.isFunction(v)) {
      // $FlowFixMe
      v = v.call(this._win, this.getPageName(pageName), bRotation === true);
    }
    //get and return array
    if (u.isString(v) && v.length) {
      // $FlowFixMe
      v = v.split(',');
    } else if (!u.isArray(v)) {
      v = [];
    }
    // $FlowFixMe
    return v;
  }

  /**
   * _processConfigData - take cares of updating caches and propagating config change
   * @param {adConfig} data - AdConfig data
   * @param {siteInfo} info - Site info
   * @returns {undefined}
   * @access private
   */
  _processConfigData(data: adConfig, info: siteInfo) {
    // update in-memory cache
    this._config = data;
    this._expiryTimeoutSec = this._config.settings.configExpirySec;
    u.extend(true, this._config, this._configOverride);

    // propagate config update to other classes
    adrotation.updateConfig(this._config.settings.adRotate);
    bm.updateConfig(this._config.settings.headerBidding);
    adserver.updateConfig(this._config.settings.adServer);
    adloader.updateConfig(this._config.settings.adloading);
    if (this._config.settings.loggerGif) {
      u.setLoggerGif(this._config.settings.loggerGif);
    }

    // update local storage cache
    storage.storeConfig(data, info.site);
  }

  /**
   * _fetchConfig - checks for fetch support and uses fetch instead of jsonp
   * @param {string} url - URL for config
   * @param {jsonpConfig} params - Extra JSONP params
   * @returns {Promise} - Fetch/JSONP promise
   * @access private
   */
  _fetchConfig(url: string, params: jsonpConfig) {
    if (fetch.isSupported()) {
      params = params || {};
      params.data = params.data || {};
      params.data['format'] = 'json';
      return fetch.JSON(url, params);
    }
    return jsonp.JSONP(url, params);
  }

  /**
   * _load - load config from server
   * @param {siteInfo} info - Site info
   * @param {boolean} bFirstLoad - Firstload?
   * @returns {Promise} - Config loading promise
   * @access private
   */
  _load(info: siteInfo, bFirstLoad: boolean) {
    let params = {};
    params.data = {
      site: info.site,
      apiVersion: u.apiVersion(),
      brand: info.brand
    };
    params.timeout = this._loadTimeout;
    params.logPrefix = 'adconfig';
    return this._fetchConfig(this._url, params).then(
      data => {
        if (data && data.settings) {
          this._processConfigData(data, info);
          this._lastConfigLoadTime = Date.now();
        }
        return data;
      },
      msg => {
        u.logError(
          `Failed to get ad config from ${this._url} for ${
            info.site
          } firstload:${bFirstLoad ? 1 : 0}: ${msg || ''}`
        );
      }
    );
  }

  /**
   * @global
   * @typedef {Object} siteInfo
   * @property {string} site - site name
   * @property {string} brand - Brand
   * @property {string} [uid] - UserID
   * @property {string} [serviceLevel] - ServiceLevel
   */
  /**
   * loads adconfig from server. If available in cache, will return cache till expiry.
   * @param {siteInfo} info - Site info
   * @return {Promise<*>} - Promise
   */
  load(info: siteInfo): Promise<*> {
    let bFirstLoad = this._lastConfigLoadTime === 0;
    let expiredCache = true;
    // get from localstorage for first time
    if (bFirstLoad) {
      let { expired, data } = storage.getConfig(info.site);
      if ('settings' in data) {
        this._processConfigData(data, info);
      }
      expiredCache = expired;
    }
    let curTime = Date.now();
    if (curTime - this._lastConfigLoadTime < this._expiryTimeoutSec * 1000) {
      return Promise.resolve();
    }
    let loadPromise = this._load(info, bFirstLoad);
    return bFirstLoad && expiredCache ? loadPromise : Promise.resolve();
  }

  /**
   * getter for config
   * @returns {adConfig} - Ad Config
   */
  get() {
    return this._config;
  }

  /**
   * @global
   * @type function
   * @callback contentHandlerCB
   * @param {string} page - pagename
   * @param {string} pos - ad position
   * @param {string} data - ad content
   */
  /**
   * @global
   * @typedef {Object} posConfigInfo
   * @property {string} as
   * @property {boolean} [aFold=false]
   * @property {string} [auid]
   * @property {number} width
   * @property {number} height
   * @property {boolean} [iframe=true]
   * @property {boolean} [gscript=false]
   * @property {boolean} [dynHeight=false]
   * @property {number}	[MarginHeight]
   * @property {contentHandlerCB}	callback
   */
  /**
   * getter for position specific config
   * @param {string} s - position name
   * @return {posConfigInfo} - Position config
   */
  getPosSettings(s: string): posConfigInfo {
    //by default we will return 1*1 adSettings
    let _def: posConfigInfo = { width: 1, height: 1, as: '', loadingMethod: 1 },
      ret = {};
    if (s.length && this._config.settings.posMap[s]) {
      ret = this._config.settings.posMap[s];
    }
    ret = u.extend(true, _def, ret);
    return ret;
  }
}

//export singleton object as default for this module
export default new _adconfig();
